// apps/server/src/match.ts
export const serverMatchConfig = {
  PREPARE_GRACE_MS: 30_000 as const,
  BUFFERED_SAMPLE_MS: 1000 as const,
  RTT_PROBE_MS: 2000 as const,
  RTT_ALPHA: 0.3 as const,
  // A socket whose ping stays unanswered this long is closed. Calibration
  // knob: lower frees a dead seat sooner; too low drops a briefly stalled
  // mobile link.
  PONG_TIMEOUT_MS: 10_000 as const,
  WS_DROP_BYTES: 64 * 1024,
  // Views are replaceable, so a slow socket gets fewer of them instead of a
  // queue of stale ones. Calibration knobs: a view is skipped while more than
  // VIEW_SKIP_BYTES wait in the socket, and once the client's view
  // acknowledgements show a per-view delay past VIEW_PACE_RTT_MS the gap
  // between views grows to half that delay. The client acks every delivered
  // view in order, so the oldest outstanding view's exact ack dates the real
  // send-to-ack delay of the weak link — the 2 s RTT probe (kept for display
  // and the `alive` frames only) could not see the queue until long after it
  // backed up.
  VIEW_SKIP_BYTES: 16 * 1024,
  VIEW_PACE_RTT_MS: 400 as const,
  // Kept under the client's starvation limit (NET_INTERP_MS + NET_STARVE_MS).
  VIEW_PACE_MAX_GAP_MS: 400 as const,
  // Messages shorter than this gain nothing from deflate. Exported so the
  // measurement tool replays the exact production compress decision.
  WS_COMPRESS_MIN_CHARS: 512 as const,
  // Worker startup must report `started` inside this budget; otherwise the room
  // aborts and releases its reservation (matches the 10 s shutdown budget).
  START_TIMEOUT_MS: 10_000 as const,
  // Failing result persistence retries at most once per this many ms.
  RECORD_RETRY_MS: 5000 as const,
  // The displayed round trip for one slot: the smoothed estimate, or the age of
  // the outstanding probe when that age clearly outlasts the estimate.
  RTT_DISPLAY_MARGIN_MS: 200 as const,
};

// apps/server/src/index.ts
export const serverAuthConfig = {
  MAX_SESSION_BODY_BYTES: 4096 as const,
};

// apps/server/src/settings.ts
export const serverSettingsConfig = {
  MAX_SETTINGS_BODY: 65536 as const,
};

// apps/server/src/observability.ts
export const serverObservabilityConfig = {
  LEVELS: { debug: 0, info: 1, warn: 2, error: 3 },
  LABEL_MAX: 64 as const,
  ERROR_TEXT_MAX: 300 as const,
  STACK_MAX: 2000 as const,
};

// apps/server/src/worker/runtime.ts
export const serverWorkerRuntimeConfig = {
  // Bounded transient-effect retention per recipient between publications.
  // Effects are filtered at their emission tick and dropped oldest-first.
  RETAINED_EFFECTS_MAX: 256 as const,
  // Flush-on-intent pacing: an applied human intent publishes a view off the
  // V2_PUBLISH_HZ cadence, but no sooner than this gap after the last
  // publication, so a spam of clicks cannot multiply bandwidth. Calibration
  // knob: one 60 Hz tick; the cadence alone costs at most two.
  VIEW_FLUSH_MIN_GAP_MS: 16 as const,
};

// apps/server/src/worker/protocol.ts
export const serverWorkerProtocolConfig = {
  // Measurement ring: worker keeps this many raw per-tick samples before
  // summarizing into a metrics message. 600 samples = 10 s at 60 Hz.
  WORKER_METRICS_RING: 600 as const,
  WORKER_TICK_INTERVAL_MS: 1000 / 60,
  // Wall time of simulation debt the worker keeps. Older debt is forgiven: no
  // tick is skipped, the match runs in slow motion instead of freezing and then
  // racing to catch up (an overloaded 8-seat match once fell 64 s behind).
  WORKER_MAX_DEBT_MS: 500 as const,
  ROOM_ID_MAX: 128 as const,
  FAILURE_DETAIL_MAX: 256 as const,
};

// apps/server/src/lobby.ts
export const serverLobbyConfig = {
  ROOM_IDLE_MS: 30 * 60 * 1000,
  REMATCH_INVITE_MS: 60_000,
  MAX_ROOMS_PER_SESSION: 8 as const,
  CREATE_MAX_PER_WINDOW: 5 as const,
  CREATE_WINDOW_MS: 60 * 1000,
  // R11: aggregate backstops. Per-guest room caps are bypassable with free
  // guests by design in M1 (accounts are M2); these global caps bound the
  // total instead. Room-code guesses are metered per IP.
  MAX_TOTAL_ROOMS: 256 as const,
  GUESS_MAX_PER_WINDOW: 60 as const,
  GUESS_WINDOW_MS: 60 * 1000,
  // R11: bounded caller admission for room creation. One caller owns at most
  // this many live rooms across all its guests; further creations get 429
  // until idle expiry frees quota. The caller key is callerIp (socket, or
  // trusted-proxy XFF-derived; resolved in apps/server/src/index.ts). Quota derives
  // from live rooms (expired via pump), so no retained keys leak beyond the
  // existing rate windows (swept every pump).
  MAX_ROOMS_PER_IP: 16 as const,
  CODE_ALPHABET: "ABCDEFGHJKMNPQRSTUVWXYZ23456789" as const,
};

export const serverConfig = {
  port: 3000,
  pumpMs: 250,
  drainMs: 60_000,
  drainMaxMs: 300_000,
  metricsPort: 0,
  metricsHost: "127.0.0.1",
  traceSampleRate: 0.1,
  traceShutdownMs: 5000,
  traceQueueSize: 2048,
  traceBatchSize: 512,
  traceExportDelayMs: 5000,
  malformedMessageLimit: 10,
  invalidMessageLimit: 20,
  adminFailuresMax: 10,
  adminFailureKeysMax: 4096,
  adminFailureWindowMs: 60000,
  roomCodeLength: 6 as const,
  roomCodeAttempts: 64,
  chatBurst: 3,
  chatRefillMs: 2000,
  pingBurst: 2,
  pingRefillMs: 3000,
  socialPreview: { width: 1200, height: 630 },
};

export const metricsBuckets = {
  httpDuration: [0.001, 0.005, 0.01, 0.025, 0.05, 0.1, 0.25, 0.5, 1, 2.5, 5],
  pumpDuration: [0.001, 0.005, 0.01, 0.025, 0.05, 0.1, 0.25, 0.5],
  pumpDelay: [0.001, 0.005, 0.01, 0.025, 0.05, 0.1, 0.25, 0.5, 1, 5],
  dbDuration: [0.0005, 0.001, 0.005, 0.01, 0.05, 0.1, 0.5, 1],
  wsReattachDelay: [0.05, 0.1, 0.25, 0.5, 1, 2.5, 5, 10, 30, 60],
  wsBuffered: [1024, 16384, 65536, 262144, 1048576, 4194304],
  wsRtt: [25, 50, 75, 100, 150, 200, 300, 400, 800, 1600],
  viewWireBytes: [1024, 4096, 16384, 65536, 262144, 1048576],
  wsSerializeSeconds: [0.00005, 0.0001, 0.00025, 0.0005, 0.001, 0.0025, 0.005, 0.01, 0.025, 0.05],
};

// Validation limits in apps/server/src/worker/protocol.ts
export const serverWorkerProtocolLimits = {
  FailureDiagnosticsSchema_errorName_maxLength: 80,
  FailureDiagnosticsSchema_stack_maxLength: 2000,
};

export const backpressureConfig = {
  closeThresholdMultiplier: 4,
};

export const tracingDefaults = {
  service: "razmak-server",
  exportTimeoutMs: 10000,
};
